import React from 'react';
import { AlertTriangle, CheckCircle, XCircle } from 'lucide-react';

const ConfirmModal = ({ 
  isOpen, onConfirm, onCancel, 
  action, transaction,
  title, message, type, confirmText, cancelText
}) => {
  if (!isOpen) return null;

  // Determine modal type and text (backward compatibility for Payments)
  const isApprove = action === 'approve';
  const isDanger = type === 'danger' || action === 'reject';
  const isSuccess = type === 'success' || action === 'approve';

  const modalTitle = title || (isApprove ? 'Approve Payment?' : 'Reject Payment?');
  const modalMessage = message || (isApprove
    ? 'This will mark the transaction as successful and activate the subscription.'
    : 'This will reject the transaction. The pharmacy will be notified.');
  const confirmBtnText = confirmText || (isApprove ? 'Yes, Approve' : 'Yes, Reject');
  const cancelBtnText = cancelText || 'Cancel';

  return (
    <div className="fixed inset-0 w-full h-full z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm" 
        onClick={onCancel}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 animate-[fadeIn_0.15s_ease-out]">
        {/* Icon */}
        <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5 ${
          isSuccess ? 'bg-green-100' : isDanger ? 'bg-red-100' : 'bg-amber-100'
        }`}>
          {isSuccess ? <CheckCircle className="text-green-600" size={36} /> : 
           isDanger ? <XCircle className="text-red-600" size={36} /> :
           <AlertTriangle className="text-amber-600" size={36} />}
        </div>

        {/* Title */}
        <h3 className="text-xl font-bold text-gray-900 text-center mb-2">
          {modalTitle}
        </h3>

        {/* Body */}
        <p className="text-gray-500 text-center text-sm mb-6">
          {modalMessage}
        </p>

        {/* Transaction Detail */}
        {transaction && (
          <div className="bg-gray-50 rounded-xl p-4 mb-6 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Pharmacy</span>
              <span className="font-semibold text-gray-900">{transaction.pharmacyName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Amount</span>
              <span className="font-semibold text-gray-900">৳{transaction.amountBdt}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Type</span>
              <span className="font-semibold text-gray-900">{transaction.type}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Reference</span>
              <span className="font-mono text-gray-700">{transaction.paymentRef || '—'}</span>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
          >
            {cancelBtnText}
          </button>
          <button
            onClick={onConfirm}
            className={`flex-1 py-3 rounded-xl text-white font-semibold transition-colors ${
              isSuccess ? 'bg-green-600 hover:bg-green-700' : 
              isDanger ? 'bg-red-500 hover:bg-red-600' : 
              'bg-amber-500 hover:bg-amber-600'
            }`}
          >
            {confirmBtnText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
